import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { Navbar } from './Navbar';
import { ClompApi } from '../api/ClompApi';
import './ClompyClicker.css';

const api = new ClompApi();

// How often clicks are sent to the backend while the game is being played
const SYNC_MS = 2000;
// With no clicks to send, time played is still sent once this many seconds have built up
const IDLE_SYNC_SECONDS = 15;

// Seconds as a length of time: 42s, 5m 07s, 2h 05m 07s
function formatDuration(seconds) {
    let pad = (n) => (n < 10 ? '0' : '') + n;
    let s = seconds % 60;
    let m = Math.floor(seconds / 60) % 60;
    let h = Math.floor(seconds / 3600);
    return h > 0 ? `${h.toLocaleString()}h ${pad(m)}m ${pad(s)}s` : m > 0 ? `${m}m ${pad(s)}s` : `${s}s`;
}

// A panel shown over the game (the shop, the stats). Closes on its button, a click outside it or Escape.
function Panel({ title, onClose, children }) {
    React.useEffect(() => {
        let onKeyDown = (e) => e.key === 'Escape' && onClose();
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [onClose]);
    return (
        <div className="clompy-panel" role="dialog" aria-modal="true" aria-label={title} onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
            <div className="clompy-panel-body">
                <h2>{title}</h2>
                {children}
                <button type="button" className="clompy-button" onClick={onClose} autoFocus>Close</button>
            </div>
        </div>
    );
}

// A cut diamond seen from the side: a flat top, a row of facets above the widest point and three running down to the tip
export function DiamondIcon() {
    return (
        <svg className="clompy-diamond" viewBox="0 0 24 24" aria-hidden="true">
            <polygon points="6,3 2,9 8,9" fill="#8fe3ff" />
            <polygon points="6,3 12,3 8,9" fill="#d4f6ff" />
            <polygon points="12,3 8,9 16,9" fill="#6fd6fb" />
            <polygon points="12,3 18,3 16,9" fill="#a9ebff" />
            <polygon points="18,3 22,9 16,9" fill="#4fb9ea" />
            <polygon points="2,9 8,9 12,22" fill="#35a7e0" />
            <polygon points="8,9 16,9 12,22" fill="#57c8f5" />
            <polygon points="16,9 22,9 12,22" fill="#2386c8" />
            <polygon points="6,3 18,3 22,9 12,22 2,9" fill="none" stroke="#1b6fa8" strokeWidth="1" strokeLinejoin="round" />
        </svg>
    );
}

export class ClompyClickerGame extends Component {
    constructor(props) {
        super(props);
        this.state = {
            showHelp: false,
            showShare: false,
            // A logged-in player's numbers as saved on the backend: diamonds they have to spend (which the backend
            // calls coins), diamonds ever earned, clicks ever made and seconds played. What is shown is these plus
            // the clicks and seconds not yet sent. A guest's stay at zero, so theirs count this visit only.
            saved: { coins: 0, allTime: 0, clicks: 0, seconds: 0 },
            pending: 0,
            pendingSeconds: 0,
            // 'shop' or 'stats' while that panel is open
            panel: null,
            // the "+1"s floating up from recent clicks: [{ id, x, y }], placed as percentages of the mug's box
            pops: [],
            pressed: false,
            // Logged-in players' diamonds are saved and count towards the leaderboard; anyone else plays for fun
            loggedIn: localStorage.getItem('token') !== null,
        }
        // clicks made, and seconds played, that a save under way is carrying
        this.sending = 0;
        this.sendingSeconds = 0;
        this.nextPop = 0;
    }

    componentDidMount() {
        if (this.state.loggedIn) {
            api.games.getClickerCoins().then((mine) => {
                this.setState({ saved: this.statsFrom(mine) });
            }).catch((err) => this.syncFailed(err));
        }
        this.timer = setInterval(this.sync, SYNC_MS);
        this.clock = setInterval(this.tick, 1000);
        document.addEventListener('visibilitychange', this.sync);
    }

    // The numbers in a backend answer
    statsFrom(answer) {
        return { coins: answer.coins, allTime: answer.allTime, clicks: answer.clicks, seconds: answer.seconds };
    }

    // Time played counts each second the game is open and on screen
    tick = () => {
        if (!document.hidden) {
            this.setState((s) => ({ pendingSeconds: s.pendingSeconds + 1 }));
        }
    }

    componentWillUnmount() {
        clearInterval(this.timer);
        clearInterval(this.clock);
        document.removeEventListener('visibilitychange', this.sync);
        this.unmounted = true;
        this.sync();
    }

    // Send the clicks made and the time played since the last save. Time alone is sent once enough has built up,
    // or when the game is being left.
    sync = () => {
        let clicks = this.state.pending;
        let seconds = this.state.pendingSeconds;
        let leaving = this.unmounted || document.hidden;
        if (!this.state.loggedIn || this.saving || (clicks === 0 && (seconds === 0 || (seconds < IDLE_SYNC_SECONDS && !leaving)))) {
            return;
        }
        this.saving = true;
        this.sending = clicks;
        this.sendingSeconds = seconds;
        if (!this.unmounted) {
            this.setState({ pending: 0, pendingSeconds: 0 });
        }
        api.games.addClickerClicks(clicks, seconds).then((result) => {
            this.saving = false;
            this.sending = 0;
            this.sendingSeconds = 0;
            if (!this.unmounted) {
                this.setState({ saved: this.statsFrom(result) });
            }
        }).catch((err) => {
            // keep the clicks and the time to try again
            let unsent = this.sending;
            let unsentSeconds = this.sendingSeconds;
            this.saving = false;
            this.sending = 0;
            this.sendingSeconds = 0;
            if (!this.unmounted) {
                this.setState((s) => ({ pending: s.pending + unsent, pendingSeconds: s.pendingSeconds + unsentSeconds }));
                this.syncFailed(err);
            }
        });
    }

    syncFailed(err) {
        if (err.response && err.response.status === 401) {
            // the login ran out: carry on as a guest with the numbers on screen
            this.setState({ loggedIn: false });
        } else {
            console.log('Error saving diamonds: ', err);
        }
    }

    closePanel = () => {
        this.setState({ panel: null });
    }

    getShareURL() {
        return window.location.origin + window.location.pathname;
    }

    toggleHelp() {
        this.setState({
            showHelp: !this.state.showHelp
        });
    }

    toggleShare() {
        this.setState({
            showShare: !this.state.showShare
        });
    }

    clompyClicked = (e) => {
        // only the main button of a mouse; touches and pens always count
        if (e.pointerType === 'mouse' && e.button !== 0) {
            return;
        }
        // the "+1" rises from where the mug was clicked
        let box = e.currentTarget.parentNode.getBoundingClientRect();
        this.click((e.clientX - box.left) / box.width * 100, (e.clientY - box.top) / box.height * 100);
    }

    // Count a click, with a "+1" at x, y (percentages of the mug's box)
    click(x, y) {
        let pop = { id: this.nextPop++, x, y };
        // only the latest few are kept, in case their animations never finish (as with reduced motion)
        this.setState((s) => ({ pending: s.pending + 1, pressed: true, pops: [...s.pops.slice(-11), pop] }));
    }

    popDone = (id) => {
        this.setState((s) => ({ pops: s.pops.filter((pop) => pop.id !== id) }));
    }

    clompyReleased = () => {
        if (this.state.pressed) {
            this.setState({ pressed: false });
        }
    }

    // Space and Enter click the mug too
    clompyKeyDown = (e) => {
        if ((e.key === ' ' || e.key === 'Enter') && !e.repeat) {
            e.preventDefault();
            this.click(40 + Math.random() * 20, 30);
        }
    }

    render() {
        // while a save is under way its clicks and seconds are in neither count
        let { saved, panel } = this.state;
        let unsaved = this.state.pending + this.sending;
        let diamonds = saved.coins + unsaved;
        let stats = [
            { label: 'Current diamonds', value: diamonds.toLocaleString(), diamond: true },
            { label: 'All-time diamonds', value: (saved.allTime + unsaved).toLocaleString(), diamond: true },
            { label: 'All-time clicks', value: (saved.clicks + unsaved).toLocaleString() },
            { label: 'Time played', value: formatDuration(saved.seconds + this.state.pendingSeconds + this.sendingSeconds) }
        ];

        let helpPopup = (
            <div className='wordle-popup' onClick={this.toggleHelp.bind(this)}>
                <div className='wordle-popup-content'>
                    <h2>How to play</h2>
                    <p>
                        The goal of the game is to collect Diamonds.
                        <br />
                        Diamonds can be collected by clicking the mug.
                        <br />
                        Diamonds can also be spent to upgrade certain attributes.
                        <br />
                        Compete against others to gather the most Diamonds.
                    </p>
                </div>
            </div>
        );

        let sharePopup = (
            <div className='wordle-popup' onClick={this.toggleShare.bind(this)}>
                <div className='wordle-popup-content'>
                    <h2>Share this game</h2>
                    <div className='button-row'>
                        <a className='wordle-button' href={`https://twitter.com/intent/tweet?text=I%20just%20played%20a%20word%20puzzle%20with%20Wordle%20on%20clomp.io!%20Check%20it%20out%20at%20${encodeURIComponent(this.getShareURL())}`} target='_blank' rel='noopener noreferrer'
                            style={
                                {
                                    backgroundColor: '#1DA1F2',
                                    color: 'white'
                                }
                            }>
                            {/* twitter icon */}
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                                <path fill="#FFFFFF" d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"></path>

                            </svg>
                        </a>
                        <div className='wordle-button' onClick={ () => {
                                navigator.clipboard.writeText(this.getShareURL());
                            }}>
                            Copy link &nbsp;
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                                <path fill="var(--color-tone-1)" d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"></path>
                            </svg>
                        </div>
                    </div>
                </div>
            </div>
        );
        return (
            <>
                <Navbar />
                <div className="wordle">
                    <div className="wordle-header">
                        <h1>Clompy Clicker</h1>
                        <div onClick={this.toggleHelp.bind(this)}>
                            <svg height="24" viewBox="0 0 24 24" width="24">
                                <path fill="var(--color-tone-1)" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"></path>
                            </svg>
                        </div>
                    </div>

                    <div className="clompyclicker">
                        <div className="game-area">
                            {/* the scene: a night sky with Clompy on a counter, light fanning out behind */}
                            <div className="clompy-stage">
                                <div className="clompy-rays" aria-hidden="true"></div>
                                <div className="clompy-diamonds" role="status" aria-label={`${diamonds.toLocaleString()} diamonds`}>
                                    <DiamondIcon />
                                    <span>{diamonds.toLocaleString()}</span>
                                </div>
                                {/* the mug sits in a box of fixed size and shrinks inside it when pressed, so nothing around it moves */}
                                <div className="clompy-mug">
                                    <img className={this.state.pressed ? 'is-pressed' : ''} src="/img/clompyclicker/clompy.svg" alt="Clompy the mug: click to collect diamonds"
                                        role="button" tabIndex="0" draggable="false"
                                        onPointerDown={this.clompyClicked} onPointerUp={this.clompyReleased} onPointerLeave={this.clompyReleased} onPointerCancel={this.clompyReleased}
                                        onKeyDown={this.clompyKeyDown} onKeyUp={this.clompyReleased} onBlur={this.clompyReleased} />
                                    {this.state.pops.map((pop) => (
                                        <span key={pop.id} className="clompy-pop" aria-hidden="true" style={{ left: `${pop.x}%`, top: `${pop.y}%` }} onAnimationEnd={() => this.popDone(pop.id)}>+1</span>
                                    ))}
                                </div>
                            </div>
                            <div className="clompy-buttons">
                                <button type="button" className="clompy-button" onClick={() => this.setState({ panel: 'shop' })}>Shop</button>
                                <button type="button" className="clompy-button" onClick={() => this.setState({ panel: 'stats' })}>Stats</button>
                                <Link className="clompy-button clompy-button-primary" to="/leaderboard?game=clompyclicker">Leaderboard</Link>
                            </div>
                            {this.state.loggedIn ? null :
                                <p className="clompy-note"><Link to="/login?next=/clompyclicker">Log in</Link> to save your diamonds and join the leaderboard.</p>
                            }
                        </div>
                    </div>

                    {panel === 'shop' ?
                        <Panel title="Shop" onClose={this.closePanel}>
                            <p className="clompy-panel-empty">Coming soon</p>
                        </Panel>
                        : null}
                    {panel === 'stats' ?
                        <Panel title="Stats" onClose={this.closePanel}>
                            <dl className="clompy-stats">
                                {stats.map((stat) => (
                                    <div key={stat.label}>
                                        <dt>{stat.label}</dt>
                                        <dd>{stat.diamond ? <DiamondIcon /> : null}{stat.value}</dd>
                                    </div>
                                ))}
                            </dl>
                            {this.state.loggedIn ? null : <p className="clompy-panel-note">These count this visit only. Log in to keep them.</p>}
                        </Panel>
                        : null}
                    {this.state.showHelp ? helpPopup : null}
                    {this.state.showShare ? sharePopup : null}
                </div>
            </>
        )
    }

}
